import { TFunction } from 'i18next';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { isDefined } from '../../../../../../utils/variables';
import { PrismData } from '../../../../../../viewer/elements/volume/model/prism-volume-model';
import { PrismVolume } from '../../../../../../viewer/elements/volume/prism-volume';
import { publish, SubscriptionTopic } from '../../../../../../viewer/messaging/pubsub';
import { useDialog } from '../../../../../hooks/use-dialog';
import { ProgressController } from '../../../../../utils/progress-controller';
import { Button } from '../../../../button/button';
import { CheckBox } from '../../../../check-box/check-box';
import { Dialog } from '../../../../dialog/dialog';
import { InfoBox } from '../../../../info-box/info-box';
import { OverlayLoader } from '../../../../overlay-loader/overlay-loader';
import { ProgressBar } from '../../../../progress-bar/progress-bar';
import { CollapsableSection } from '../../../../skyview/right-menu/components/collapsable-section';
import { CollapsableSectionGroup } from '../../../../skyview/right-menu/components/collapsable-section-group';
import {
  RightPanelContent,
  RightPanelFooter,
} from '../../../../skyview/right-menu/components/styles';
import { Stack } from '../../../../stack/stack';
import { reportHelpers } from '../helpers';
import { NoVolumesPanel } from '../helpers/no-volumes-panel';
import { useVolumesWithAssets } from '../helpers/use-volume-with-assets';
import { VolumeWithAsset } from '../helpers/utils';
import {
  LoadedOverviewUrl,
  LoadedPrismData,
  loadOptionalPrismVolumeReportData,
} from './load-optional-report-data';
import {
  maxPageCountPrismReport,
  maxPrismCountPrismReport,
  PrismPdfReportPreview,
} from './prism-pdf';
import { VolumeSingleSelectListSection } from './prism-volume-list';

export const PrismPdfReportMenu = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectedVolume, setSelectedVolume] = useState<VolumeWithAsset>();
  const prismFilter = React.useCallback(
    (volume: VolumeWithAsset) => volume.volume instanceof PrismVolume,
    [],
  );
  const { volumesWithAssets } = useVolumesWithAssets(prismFilter);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState<string>();
  const [includeOverview, setIncludeOverview] = useState(true);
  const [includePrismList, setIncludePrismList] = useState(true);
  const [prismData, setPrismData] = useState<LoadedPrismData | undefined>();
  const [overviewUrl, setOverviewUrl] = useState<LoadedOverviewUrl | undefined>();
  const dialog = useDialog();

  const prismVolume =
    selectedVolume?.volume instanceof PrismVolume ? selectedVolume.volume : undefined;
  const displayMaxPrismCountExceededWarning =
    includePrismList && (prismVolume?.prismCount ?? 0) > maxPrismCountPrismReport;

  const onClickPreviewButton = async () => {
    try {
      setLoading(true);
      const progressController = new ProgressController((progress, text) => {
        setProgress(progress);
        setProgressText(text);
      });

      progressController.reset();
      const data = await loadOptionalPrismVolumeReportData(selectedVolume, prismData, overviewUrl, {
        includeOverview,
        includePrismList,
        progressController,
      });
      setOverviewUrl(data?.overviewUrl);
      setPrismData(data?.prismData);

      dialog.show();
    } finally {
      setLoading(false);
    }
  };

  const onClickExportReportButton = async () => {
    if (!isDefined(selectedVolume)) {
      throw new Error('No asset selected');
    }

    const progressController = new ProgressController((progress, text) => {
      setProgress(progress);
      setProgressText(text);
    });
    progressController.reset();

    const options = {
      includeOverview,
      includePrismList,
      progressController,
    };

    try {
      setLoading(true);
      const data = await loadOptionalPrismVolumeReportData(
        selectedVolume,
        prismData,
        overviewUrl,
        options,
      );
      setPrismData(data?.prismData);
      setOverviewUrl(data?.overviewUrl);

      await exportReport(
        selectedVolume,
        {
          prismData: data?.prismData?.data,
          overviewUrl: data?.overviewUrl?.url,
        },
        progressController,
        t,
      );
    } finally {
      setLoading(false);
    }
  };

  if (volumesWithAssets.length === 0) {
    return <NoVolumesPanel />;
  }

  return (
    <>
      <RightPanelContent useTopPadding={false}>
        <OverlayLoader backgroundColor="#FFFA" showIcon={false} visible={loading}>
          <CollapsableSectionGroup>
            <SettingsSection
              includeOverview={includeOverview}
              includePrismList={includePrismList}
              setIncludeOverview={setIncludeOverview}
              setIncludePrismList={setIncludePrismList}
            />

            <VolumeSingleSelectListSection
              loading={loading}
              selectedVolume={selectedVolume}
              setSelectedVolume={setSelectedVolume}
              volumesWithAssets={volumesWithAssets}
            />
          </CollapsableSectionGroup>
        </OverlayLoader>
      </RightPanelContent>

      <RightPanelFooter>
        <Stack direction="column" spacing={0.4}>
          <Button
            color={'secondary'}
            disabled={loading || !isDefined(selectedVolume)}
            leftIcon={{ icon: 'print-magnifying-glass' }}
            loading={loading}
            variant={'contained'}
            width="100%"
            onClick={onClickPreviewButton}
          >
            {t('export.preview', { ns: 'skyviewReport' })}
          </Button>

          {loading && <ProgressBar max={1} min={0} text={progressText} value={progress} />}

          <Button
            color={'primary'}
            disabled={loading || !isDefined(selectedVolume)}
            loading={loading}
            variant={'contained'}
            width="100%"
            onClick={onClickExportReportButton}
          >
            {t('exportButton', { ns: 'skyviewReport' })}
          </Button>

          <InfoBox color="yellow">
            {t('export.selectedVolume', { ns: 'skyviewReport' })}
            <SelectedVolume volume={selectedVolume} />
          </InfoBox>

          {displayMaxPrismCountExceededWarning && <MaxPrismCountExceededWarning />}
        </Stack>
      </RightPanelFooter>

      {selectedVolume &&
        dialog.render(
          <Dialog closeIcon={true} maxHeight={'expand'} width={900} onClose={() => dialog.hide()}>
            {{
              header: t('preview.dialogHeader', { ns: 'skyviewReport' }),
              content: (
                <PrismPdfReportPreview
                  overviewPictureDataUrl={includeOverview ? overviewUrl?.url : undefined}
                  prismData={includePrismList ? prismData?.data : undefined}
                  volumeWithAsset={selectedVolume}
                />
              ),
              footer: {
                right: (
                  <>
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={async () => {
                        dialog.hide();
                        await onClickExportReportButton();
                      }}
                    >
                      {t('exportButton', { ns: 'skyviewReport' })}
                    </Button>
                    <Button variant="text" onClick={dialog.hide}>
                      {t('close', { ns: 'common' })}
                    </Button>
                  </>
                ),
              },
            }}
          </Dialog>,
        )}
    </>
  );
};

const SettingsSection = (props: {
  includeOverview: boolean;
  setIncludeOverview: (state: boolean) => void;
  includePrismList: boolean;
  setIncludePrismList: (state: boolean) => void;
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  return (
    <CollapsableSection
      expanded={expanded}
      title={t('settings', { ns: 'skyviewReport' })}
      titleUpperCase={true}
      onSetExpanded={setExpanded}
    >
      <Stack direction="column" spacing={1}>
        <CheckBox
          checked={props.includeOverview}
          onChange={() => props.setIncludeOverview(!props.includeOverview)}
        >
          {t('settingsPrism.includeOverview', { ns: 'skyviewReport' })}
        </CheckBox>

        <CheckBox
          checked={props.includePrismList}
          onChange={() => props.setIncludePrismList(!props.includePrismList)}
        >
          {t('settingsPrism.includePrismList', { ns: 'skyviewReport' })}
        </CheckBox>
      </Stack>
    </CollapsableSection>
  );
};

const SelectedVolume = (props: { volume?: VolumeWithAsset }) => {
  const { t } = useTranslation();
  if (!isDefined(props.volume)) {
    return (
      <>
        : <NoSelectionLabel>{t('export.noSelection', { ns: 'skyviewReport' })}</NoSelectionLabel>
      </>
    );
  }

  return (
    <>
      <br />
      <strong>{props.volume.volume.name!}</strong>
      <br />
      <strong>
        ({props.volume.asset.name} - {props.volume.sourceName!})
      </strong>
    </>
  );
};

const MaxPrismCountExceededWarning = () => {
  const { t } = useTranslation();
  return (
    <InfoBox color="yellow" leftIcon={{ icon: 'exclamation' }}>
      {t('prismReport.maxPrismCountExceededWarning', {
        ns: 'skyviewReport',
        maxPageCountPrismReport: maxPageCountPrismReport,
        maxPrismCountPrismReport: maxPrismCountPrismReport,
      })}
    </InfoBox>
  );
};

async function exportReport(
  selectedVolume: VolumeWithAsset,
  data: { prismData?: PrismData[]; overviewUrl?: string },
  progressController: ProgressController,
  t: TFunction,
) {
  try {
    progressController.callback(
      1,
      t('prismReport.generatingReport', { ns: 'skyviewReport' }) as string,
    );
    await reportHelpers.exportPrismPdfReport(selectedVolume, data.prismData, data.overviewUrl);
    progressController.callback(1);
  } catch (error) {
    publish(SubscriptionTopic.ToastrMessage, {
      message: t('exportError', { ns: 'skyviewReport' }) as string,
      type: 'error',
    });
  }
}

const NoSelectionLabel = styled.label`
  font-weight: 300;
`;
